import { registerBlockType } from '@wordpress/blocks'
import { __ } from '@wordpress/i18n'
import classnames from 'classnames'

import {
	useBlockProps,
	InnerBlocks,
	AlignmentToolbar,
	BlockControls,
	InspectorControls,
	MediaUpload,
	MediaUploadCheck,
	MediaReplaceFlow,
	__experimentalBlock as Block,
	__experimentalUseGradient,
	__experimentalPanelColorGradientSettings as PanelColorGradientSettings,
	__experimentalBlockAlignmentMatrixControl as BlockAlignmentMatrixControl,
	__experimentalBlockFullHeightAligmentControl as FullHeightAlignmentControl,
	BlockVerticalAlignmentControl
} from '@wordpress/block-editor'

import {
	PanelBody,
	TabPanel,
	RangeControl,
	SelectControl,
	ToggleControl,
	ColorPalette,
	Button,
	ResizableBox,
	ResponsiveWrapper,
	FocalPointPicker,
	DuotoneControl,
	DuotoneSwatch,
	__experimentalHeading as Heading,
	__experimentalBoxControl as BoxControl,
	__experimentalSpacer as Spacer,
	__experimentalDivider as Divider,
	__experimentalToolsPanel as ToolsPanel,
	__experimentalToolsPanelItem as ToolsPanelItem
} from '@wordpress/components'

// import { Divider } from '@wordpress/components/ui'

import {
	Component,
	useRef,
	useState
} from '@wordpress/element'

import { select, withSelect } from '@wordpress/data'

const getStyles = (props) => {
	return {
		root: {
			padding: {
				paddingTop: props.attributes.boxValuesPadding.top,
				paddingRight: props.attributes.boxValuesPadding.right,
				paddingBottom: props.attributes.boxValuesPadding.bottom,
				paddingLeft: props.attributes.boxValuesPadding.left
			},
			background: {
				backgroundColor: props.attributes.backgroundColor,
				backgroundImage: 'url(' + props.attributes.mediaUrl + ')',
				backgroundSize: 'cover',
				backgroundPositionX: props.attributes.focalPoint.x * 100 + '%',
				backgroundPositionY: props.attributes.focalPoint.y * 100 + '%'
			},
			misc: {
				minHeight: props.attributes.minHeight + 'px'
			}
		},
		video: {
			objectPosition: props.attributes.focalPoint.x * 100 + '% ' + props.attributes.focalPoint.y * 100 + '%'
		},
		overlay: {
			backgroundColor: props.attributes.overlayBackgroundColor,
			backgroundImage: props.attributes.overlayBackgroundImage,
			mixBlendMode: props.attributes.overlayMixBlendMode,
			opacity: props.attributes.overlayOpacity / 100
		}
	}
}
const extendBlockProps = (props) => {
	return {
		className: [
			'wp-bootblock',
			props.attributes.parallax ? 'wp-bootblock-is-parallax' : '',
			props.attributes.verticalAlignment ? 'is-vertically-aligned-' + props.attributes.verticalAlignment : ''
		].join(' '),
		style: {
			...getStyles(props).root.padding,
			...getStyles(props).root.misc,
			...(props.attributes.mediaType === 'image' ? getStyles(props).root.background : {})
		}
	}
}
registerBlockType('wp-bootblock/section', {
	apiVersion: 2,
	title: __('Section – Bootblock ', 'wp-bootblock'),
	description: __('A section layout element.', 'wp-bootblock'),
	keywords: ['section'],
	icon: 'images-alt',
	category: 'layout',
	supports: {
		align: ['full', 'wide']
	},
	attributes: {
		verticalAlignment: {
			type: 'string',
			default: ''
		},
		duotone: {
			type: 'string',
			default: ''
		},
		minHeight: {
			type: 'number',
			default: 0
		},
		padding: {
			type: 'number',
			default: 2
		},
		mediaType: {
			type: 'string',
			default: 'image'
		},
		mediaId: {
			type: 'number',
			default: 0
		},
		mediaUrl: {
			type: 'string',
			default: ''
		},
		alignment: {
			type: 'string',
			default: 'none'
		},
		backgroundColor: {
			type: 'string',
			default: ''
		},
		overlayBackgroundColor: {
			type: 'string',
			default: ''
		},
		overlayBackgroundImage: {
			type: 'string',
			default: ''
		},
		overlayMixBlendMode: {
			type: 'string',
			default: ''
		},
		overlayOpacity: {
			type: 'number',
			default: 50
		},
		focalPoint: {
			type: 'object',
			default: {
				x: .5,
				y: .5
			}
		},
		parallax: {
			type: 'boolean',
			default: false
		},
		boxValuesPadding: {
			default: {
				top: '2rem',
				right: '0rem',
				bottom: '2rem',
				left: '0rem'
			}
		}
	},
	edit: withSelect((select, props) => {
		return {media: props.attributes.mediaId ? select('core').getMedia(props.attributes.mediaId) : undefined}
	})(
		(props) => {

			const {
				gradientClass,
				gradientValue,
				setGradient
			} = __experimentalUseGradient()

			console.log('attributes: ', props.attributes)
			console.log('props: ', props)

			const colors = select('core/block-editor').getSettings().colors

			const onChangeAlignment = (newAlignment) => {
				props.setAttributes({
					alignment: newAlignment === undefined ? 'none' : newAlignment
				})
			}

			const removeMedia = () => {
				props.setAttributes({
					mediaId: 0,
					mediaUrl: ''
				})
			}

			const onSelectMedia = (media) => {
				console.log('media: ', media)
				props.setAttributes({
					mediaId: media.id,
					mediaUrl: media.url,
					mediaType: media.mime.match('video.*') ? 'video' : 'image'
				})
			}

			const onSetFocalPoint = (point) => {
				props.setAttributes({
					focalPoint: point.focalPoint
				})
			}

			const onSetFocalPointOnDrag = (point) => {
				props.setAttributes({
					focalPoint: point.focalPoint
				})
			}

			const onChangeParallax = (checked) => {
				props.setAttributes({parallax: checked})
				props.setAttributes({focalPoint: {x: .5, y: .5}})
			}

			const DUOTONE_PALETTE = [
				{colors: ['#8C00B7', '#FCFF41'], name: 'Purple and yellow', slug: 'purple-yellow'},
				{colors: ['#000097', '#FF4747'], name: 'Blue and red', slug: 'blue-red'}
			]

			const COLOR_PALETTE = [
				{color: '#FF4747', name: 'Red', slug: 'red'},
				{color: '#FCFF41', name: 'Yellow', slug: 'yellow'},
				{color: '#000097', name: 'Blue', slug: 'blue'},
				{color: '#8C00B7', name: 'Purple', slug: 'purple'}
			]

			const [isResizing, setIsResizing] = useState(false)

			return (
				<>
					<BlockControls>
						<AlignmentToolbar
							value={props.attributes.alignment}
							onChange={onChangeAlignment}
						/>
						<BlockVerticalAlignmentControl
							onChange={(val) => { props.setAttributes({verticalAlignment: val}) }}
							value={props.attributes.verticalAlignment}
						/>
					</BlockControls>

					<BlockControls group="block">
						{/*<BlockAlignmentMatrixControl*/}
						{/*	label={__('Change content position')}*/}
						{/*	value={props.attributes.contentPosition}*/}
						{/*	onChange={(val) => {*/}
						{/*		console.log('val: ', val)*/}
						{/*		props.setAttributes({contentPosition: val})*/}
						{/*	}}*/}
						{/*/>*/}
						{/*<FullHeightAlignmentControl*/}
						{/*	isActive={ isMinFullHeight }*/}
						{/*	onToggle={ toggleMinFullHeight }*/}
						{/*	isDisabled={ ! hasInnerBlocks }*/}
						{/*/>*/}
					</BlockControls>

					<BlockControls group="other">
						<MediaReplaceFlow
							mediaId={props.attributes.mediaId}
							mediaURL={props.attributes.mediaUrl}
							allowedTypes={['image', 'video']}
							accept="image/*,video/*"
							onSelect={onSelectMedia}
							name={!props.attributes.mediaUrl ? __('Add Media') : __('Replace')}
						/>
					</BlockControls>

					<InspectorControls>

						<TabPanel
							className="my-tab-panel"
							tabs={[
								{
									name: 'tab-bg',
									title: __('Background', 'wp_bootstrap'),
									className: 'tab-bg'
								},
								{
									name: 'tab-size',
									title: 'Size',
									className: 'tab-size'
								}
							]}
						>

							{(tab) => {
								if (tab.name === 'tab-bg') {
									return (
										<>
											<Spacer></Spacer>

											<PanelBody
												title={__('Background', 'awp')}
												initialOpen={true}
											><Spacer>
												<MediaUploadCheck>
													<MediaUpload
														onSelect={onSelectMedia}
														value={props.attributes.mediaId}
														allowedTypes={['image']}
														render={({open}) => (
															<Button
																className={props.attributes.mediaId === 0 ? 'editor-post-featured-image__toggle' : 'editor-post-featured-image__preview'}
																onClick={open}
															>
																{props.attributes.mediaId === 0 && __('Choose an image', 'awp')}
															</Button>
														)}
													/>
												</MediaUploadCheck>
											</Spacer>

												{props.attributes.mediaType === 'image' &&
												<ToggleControl
													label={__('Parallax Background', 'wp_bootstrap')}
													checked={props.attributes.parallax}
													onChange={onChangeParallax}
												/>
												}

												{props.attributes.mediaId !== 0 && !props.attributes.parallax &&
												<>
													<FocalPointPicker
														url={props.attributes.mediaUrl}
														dimensions={{width: 400, height: 100}}
														value={props.attributes.focalPoint}
														onDrag={(focalPoint) => onSetFocalPointOnDrag({focalPoint})}
														onChange={(focalPoint) => onSetFocalPoint({focalPoint})}
													/>
													<div style={{
														display: 'flex',
														justifyContent: 'flex-end'
													}}>
														<Button onClick={removeMedia} isDestructive>{__('Remove media', 'awp')}</Button>
													</div>
												</>
												}
												<Divider/>

												<Spacer>
													<Heading level={5}>Backgroundcolor</Heading>
												</Spacer>
												<ColorPalette
													colors={colors}
													value={props.attributes.color}
													onChange={(val) => props.setAttributes({backgroundColor: val})}
												/>
											</PanelBody>

											<PanelColorGradientSettings
												__experimentalHasMultipleOrigins
												title={__('Overlay')}
												initialOpen={true}
												settings={[
													{
														colorValue: props.attributes.overlayBackgroundColor,
														gradientValue,
														onColorChange: (val) => props.setAttributes({overlayBackgroundColor: val}),
														onGradientChange: (val) => props.setAttributes({overlayBackgroundImage: val}),
														label: __('Color')
													}
												]}>
												<RangeControl
													label={__('Opacity')}
													value={props.attributes.overlayOpacity}
													onChange={(val) => props.setAttributes({overlayOpacity: val})}
													min={0}
													max={100}
													step={10}
													required
												/>

												<SelectControl
													label={__('Blend mode:')}
													value={props.attributes.overlayMixBlendMode}
													onChange={(val) => props.setAttributes({overlayMixBlendMode: val})}
													options={[
														{value: 'unset', label: 'None'},
														{value: 'normal', label: 'normal'},
														{value: 'multiply', label: 'multiply'},
														{value: 'screen', label: 'screen'},
														{value: 'overlay', label: 'overlay'},
														{value: 'darken', label: 'darken'},
														{value: 'lighten', label: 'lighten'},
														{value: 'color-dodge', label: 'color-dodge'},
														{value: 'color-burn', label: 'color-burn'},
														{value: 'hard-light', label: 'hard-light'},
														{value: 'soft-light', label: 'soft-light'},
														{value: 'difference', label: 'difference'},
														{value: 'exclusion', label: 'exclusion'},
														{value: 'hue', label: 'hue'},
														{value: 'saturation', label: 'saturation'},
														{value: 'color', label: 'color'},
														{value: 'luminosity', label: 'luminosity'}
													]}
												/>
												{/*<DuotoneControl*/}
												{/*	duotonePalette={DUOTONE_PALETTE}*/}
												{/*	colorPalette={COLOR_PALETTE}*/}
												{/*	value={props.attributes.duotone}*/}
												{/*	onChange={(val) => { props.setAttributes({duotone: val}) }}*/}
												{/*/>*/}
												{/*<DuotoneSwatch values={props.attributes.duotone}/>*/}
											</PanelColorGradientSettings>
										</>
									)
								}
								if (tab.name === 'tab-size') {
									return (
										<>
											<BoxControl
												label={__('Padding', 'wp_bootstrap')}
												values={props.attributes.boxValuesPadding}
												onChange={(val) => props.setAttributes({boxValuesPadding: val})}
											/>
										</>
									)
								}
							}}
						</TabPanel>
					</InspectorControls>

					<svg id="Layer_1" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 380.56 168.57">
						<path
							d="M26.13,450.41a6.14,6.14,0,1,0,12.27,0v-7.36a6.14,6.14,0,0,1,12.28,0v13.5a6.14,6.14,0,0,0,12.28,0V433.81a6.14,6.14,0,1,1,12.27,0v10.47a6.14,6.14,0,1,0,12.28,0v-3.91a6.14,6.14,0,1,1,12.27,0v13.49a6.14,6.14,0,0,0,12.28,0V431.12a6.14,6.14,0,0,1,12.28,0v19.29a6.14,6.14,0,1,0,12.27,0v-7.36a6.14,6.14,0,1,1,12.28,0v13.5a6.14,6.14,0,0,0,12.27,0V433.81a6.14,6.14,0,1,1,12.28,0v10.47a6.14,6.14,0,1,0,12.28,0v-3.91a6.14,6.14,0,1,1,12.27,0v13.49a6.14,6.14,0,0,0,12.28,0V431.12a6.14,6.14,0,0,1,12.27,0v19.29a6.14,6.14,0,1,0,12.28,0v-7.36a6.14,6.14,0,1,1,12.28,0v13.5a6.14,6.14,0,0,0,12.27,0V433.81a6.14,6.14,0,1,1,12.28,0v10.47a6.14,6.14,0,1,0,12.27,0v-3.91a6.14,6.14,0,0,1,12.28,0v13.49a6.14,6.14,0,0,0,12.28,0V431.12a6.14,6.14,0,0,1,12.27,0v19.29a6.14,6.14,0,0,0,12.28,0v-7.36a6.14,6.14,0,1,1,12.27,0v13.5a6.14,6.14,0,0,0,12.28,0V433.81a6.14,6.14,0,0,1,12.28,0v10.47a6.14,6.14,0,1,0,12.27,0v-3.91a6.14,6.14,0,0,1,12.28,0v13.49a6.14,6.14,0,1,0,12.27,0V302.94a6.14,6.14,0,1,0-12.27,0v13.49a6.14,6.14,0,1,1-12.28,0v-3.91a6.14,6.14,0,1,0-12.27,0V323a6.14,6.14,0,1,1-12.28,0V300.25a6.14,6.14,0,0,0-12.28,0v13.5a6.14,6.14,0,1,1-12.27,0v-7.36a6.14,6.14,0,0,0-12.28,0v19.29a6.14,6.14,0,0,1-12.27,0V302.94a6.14,6.14,0,0,0-12.28,0v13.49a6.14,6.14,0,0,1-12.28,0v-3.91a6.14,6.14,0,1,0-12.27,0V323a6.14,6.14,0,0,1-12.28,0V300.25a6.14,6.14,0,0,0-12.27,0v13.5a6.14,6.14,0,1,1-12.28,0v-7.36a6.14,6.14,0,1,0-12.28,0v19.29a6.14,6.14,0,0,1-12.27,0V302.94a6.14,6.14,0,0,0-12.28,0v13.49a6.14,6.14,0,1,1-12.27,0v-3.91a6.14,6.14,0,1,0-12.28,0V323a6.14,6.14,0,1,1-12.28,0V300.25a6.14,6.14,0,0,0-12.27,0v13.5a6.14,6.14,0,0,1-12.28,0v-7.36a6.14,6.14,0,1,0-12.27,0v19.29a6.14,6.14,0,0,1-12.28,0V302.94a6.14,6.14,0,0,0-12.28,0v13.49a6.14,6.14,0,1,1-12.27,0v-3.91a6.14,6.14,0,0,0-12.28,0V323A6.14,6.14,0,1,1,63,323V300.25a6.14,6.14,0,0,0-12.28,0v13.5a6.14,6.14,0,0,1-12.28,0v-7.36a6.14,6.14,0,1,0-12.27,0Z"
							transform="translate(-26.13 -294.12)"/>
					</svg>

					<section {...useBlockProps(extendBlockProps(props))}>
						<div id="clipPaths">
							<svg className="svg">
								<clipPath id="cp1" clipPathUnits="objectBoundingBox">
									<path d="M0,1 V0.088 L1,0.015 V0.941"></path>
								</clipPath>
							</svg>
							<svg className="svg">
								<clipPath id="cp2" clipPathUnits="objectBoundingBox">
									<path
										d="M0.069,2 a0.016,0.025,0,1,0,0.032,0 v-0.03 a0.016,0.025,0,0,1,0.032,0 v0.054 a0.016,0.025,0,0,0,0.032,0 V2 a0.016,0.025,0,1,1,0.032,0 v0.042 a0.016,0.025,0,0,0,0.032,0 v-0.016 a0.016,0.025,0,1,1,0.032,0 v0.054 a0.016,0.025,0,1,0,0.032,0 V2 a0.016,0.025,0,0,1,0.032,0 v0.078 a0.016,0.025,0,1,0,0.032,0 v-0.03 a0.016,0.025,0,0,1,0.032,0 v0.054 a0.016,0.025,0,0,0,0.032,0 V2 a0.016,0.025,0,1,1,0.032,0 v0.042 a0.016,0.025,0,1,0,0.032,0 v-0.016 a0.016,0.025,0,1,1,0.032,0 v0.054 a0.016,0.025,0,0,0,0.032,0 V2 a0.016,0.025,0,0,1,0.032,0 v0.078 a0.016,0.025,0,1,0,0.032,0 v-0.03 a0.016,0.025,0,1,1,0.032,0 v0.054 a0.016,0.025,0,0,0,0.032,0 V2 a0.016,0.025,0,0,1,0.032,0 v0.042 a0.016,0.025,0,1,0,0.032,0 v-0.016 a0.016,0.025,0,1,1,0.032,0 v0.054 a0.016,0.025,0,0,0,0.032,0 V2 a0.016,0.025,0,0,1,0.032,0 v0.078 a0.016,0.025,0,0,0,0.032,0 v-0.03 a0.016,0.025,0,1,1,0.032,0 v0.054 a0.016,0.025,0,0,0,0.032,0 V2 a0.016,0.025,0,0,1,0.032,0 v0.042 a0.016,0.025,0,1,0,0.032,0 v-0.016 a0.016,0.025,0,0,1,0.032,0 v0.054 a0.016,0.025,0,1,0,0.032,0 V1 a0.016,0.025,0,1,0,-0.032,0 v0.054 a0.016,0.025,0,1,1,-0.032,0 v-0.016 a0.016,0.025,0,1,0,-0.032,0 V1 a0.016,0.025,0,1,1,-0.032,0 V1 a0.016,0.025,0,0,0,-0.032,0 v0.054 a0.016,0.025,0,1,1,-0.032,0 v-0.03 a0.016,0.025,0,0,0,-0.032,0 v0.078 a0.016,0.025,0,0,1,-0.032,0 V1 a0.016,0.025,0,0,0,-0.032,0 v0.054 a0.016,0.025,0,0,1,-0.032,0 v-0.016 a0.016,0.025,0,1,0,-0.032,0 V1 a0.016,0.025,0,0,1,-0.032,0 V1 a0.016,0.025,0,0,0,-0.032,0 v0.054 a0.016,0.025,0,1,1,-0.032,0 v-0.03 a0.016,0.025,0,1,0,-0.032,0 v0.078 a0.016,0.025,0,0,1,-0.032,0 V1 a0.016,0.025,0,0,0,-0.032,0 v0.054 a0.016,0.025,0,1,1,-0.032,0 v-0.016 a0.016,0.025,0,1,0,-0.032,0 V1 a0.016,0.025,0,1,1,-0.032,0 V1 a0.016,0.025,0,0,0,-0.032,0 v0.054 a0.016,0.025,0,0,1,-0.032,0 v-0.03 a0.016,0.025,0,1,0,-0.032,0 v0.078 a0.016,0.025,0,0,1,-0.032,0 V1 a0.016,0.025,0,0,0,-0.032,0 v0.054 a0.016,0.025,0,1,1,-0.032,0 v-0.016 a0.016,0.025,0,0,0,-0.032,0 V1 A0.016,0.025,0,1,1,0.166,1 V1 a0.016,0.025,0,0,0,-0.032,0 v0.054 a0.016,0.025,0,0,1,-0.032,0 v-0.03 a0.016,0.025,0,1,0,-0.032,0"></path>
								</clipPath>
							</svg>

						</div>
						<ResizableBox
							className={classnames({
								'is-resizing': isResizing
							})}
							enable={{
								top: false,
								right: false,
								bottom: true,
								left: false,
								topRight: false,
								bottomRight: false,
								bottomLeft: false,
								topLeft: false
							}}
							onResize={(event, direction, elt, delta) => {
								props.setAttributes({
									minHeight: parseInt(elt.clientHeight, 10)
								})
								if (!isResizing) {
									setIsResizing(true)
								}
							}}
							onResizeStop={(event, direction, elt, delta) => {
								setIsResizing(false)
								props.setAttributes({
									minHeight: parseInt(elt.clientHeight, 10)
								})
							}}
						>
						</ResizableBox>
						<span className="wp-block-overlay" role="presentation" style={getStyles(props).overlay}></span>
						{props.attributes.mediaType === 'video' &&
						<>
							<video className="wp-bootblock-video-bg"
										 style={getStyles(props).video}
										 autoPlay muted loop
										 src={props.attributes.mediaUrl}
							/>
						</>
						}
						<div className="container inner-container">
							<InnerBlocks/>
						</div>
					</section>

				</>
			)
			// }
		}
	),
	save: (props) => {
		const blockProps = useBlockProps.save(extendBlockProps(props))
		const id = props.attributes.id
		console.log('props: ', props)

		return (
			<section {...blockProps}>
				<span className="wp-block-overlay" role="presentation" style={getStyles(props).overlay}></span>
				<div className="container inner-container">
					<InnerBlocks.Content/>
				</div>
			</section>
		)
	}
})
